import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { fetchMessages, getAvatarUrl } from "../api/whatsapp.js";
import { fetchShortcuts } from "../api/shortcuts.ts";
import { useAppActions } from "../app/AppProvider.tsx";
import { useToast } from "../components/ToastProvider.tsx";
import type { TextShortcut, WhatsappChat } from "../types.ts";
import {
  avatarLookupJidForChat,
  avatarPatchForChat,
  aliasJidsForChat,
  buildShowLeadPatch,
  chatPatchFromCentralLead,
  displayNameForChat,
  ensureChatAccount,
  findLeadForChat,
  hydrateChatFromKanbanLead,
  mergeMessagePages,
  oldestLoadedMessageTs,
  shouldAutoLoadChatHistory,
  sortChatsForList,
} from "./Chat.helpers.ts";
import { CentralLeadCard, type CentralLead } from "./CentralLeadCard.tsx";
import { Composer } from "./chat-room/Composer.tsx";
import { KanbanLeadCard } from "./chat-room/KanbanLeadCard.tsx";
import { MessagesPane } from "./chat-room/MessagesPane.tsx";
import { RoomHeader } from "./chat-room/RoomHeader.tsx";
import { TagPicker } from "./chat-room/TagPicker.tsx";
import type { TagPickerState } from "./chat-room/types.ts";

const MESSAGE_PAGE_LIMIT = 50;

export function ChatRoom() {
  const { state, dispatch, patch } = useAppActions();
  const { showToast } = useToast();
  const [tagPicker, setTagPicker] = useState<TagPickerState | null>(null);
  const [avatarUrls, setAvatarUrls] = useState<Record<string, string | null>>({});
  const [avatarLoading, setAvatarLoading] = useState<Record<string, boolean>>({});
  const [olderLoading, setOlderLoading] = useState<Record<string, boolean>>({});
  const [historyHasMore, setHistoryHasMore] = useState<Record<string, boolean>>({});
  const shortcutLoadRef = useRef<Promise<TextShortcut[]> | null>(null);
  const historyLoadRef = useRef<Set<string>>(new Set());

  const chatJid = state.activeChatJid;
  const rawChat = useMemo(
    () => state.chats.find((item) => item.chatJid === chatJid) || { chatJid },
    [state.chats, chatJid],
  );
  const lead = useMemo(
    () => findLeadForChat(rawChat, state.leads || []),
    [rawChat, state.leads],
  );
  const chat = useMemo(
    () => ensureChatAccount(
      hydrateChatFromKanbanLead(rawChat, lead),
      state.accounts || [],
      state.accountFilter || [],
      state.whatsappSession,
    ),
    [rawChat, lead, state.accounts, state.accountFilter, state.whatsappSession],
  );
  const messages = state.chatMessages[chatJid] || [];
  const historyAliasJids = useMemo(
    () => aliasJidsForChat(chat, state.chats || []),
    [chat, state.chats],
  );
  const historyLoadKey = useMemo(
    () => [chat.chatJid, ...historyAliasJids].join("|"),
    [chat.chatJid, historyAliasJids],
  );
  const canAutoLoadHistory = shouldAutoLoadChatHistory(chat, messages) || historyAliasJids.length > 0;
  const activeAvatarUrl = avatarUrls[chat.chatJid] ?? (typeof chat.avatarUrl === "string" ? chat.avatarUrl : null);
  const activeAvatarLookupJid = avatarLookupJidForChat(chat);

  const applyCentralLeadToChat = useCallback((centralLead: CentralLead) => {
    const chatPatch = chatPatchFromCentralLead(chat, centralLead);
    if (!Object.keys(chatPatch).length) return;
    dispatch({ type: "PATCH_CHAT", chat: { chatJid: chat.chatJid, ...chatPatch } });
  }, [chat, dispatch]);

  useEffect(() => {
    if (!chatJid || !canAutoLoadHistory || historyLoadRef.current.has(historyLoadKey)) return;
    const currentChatJid = chat.chatJid;
    historyLoadRef.current.add(historyLoadKey);
    let active = true;

    fetchMessages(currentChatJid, { limit: MESSAGE_PAGE_LIMIT, aliasJids: historyAliasJids })
      .then((next) => {
        if (!active) return;
        dispatch({ type: "SET_CHAT_MESSAGES", chatJid: currentChatJid, messages: next });
        setHistoryHasMore((prev) => ({ ...prev, [currentChatJid]: next.length >= MESSAGE_PAGE_LIMIT }));
      })
      .catch((err) => {
        if (active) {
          showToast(err instanceof Error ? err.message : "Falha ao carregar historico", "error");
        }
      });

    return () => {
      active = false;
    };
  }, [canAutoLoadHistory, chat.chatJid, chatJid, dispatch, historyAliasJids, historyLoadKey, showToast]);

  useEffect(() => {
    if (!chatJid || activeAvatarUrl || !activeAvatarLookupJid) return;
    let active = true;

    async function autoSyncProfilePicture() {
      const lookupJid = activeAvatarLookupJid;
      const nextUrl = await getAvatarUrl(lookupJid, { syncAvatar: true });
      if (!active || !nextUrl) return;
      setAvatarUrls((prev) => ({ ...prev, [chat.chatJid]: nextUrl }));
      dispatch({ type: "PATCH_CHAT", chat: avatarPatchForChat(chat, nextUrl) });
    }

    autoSyncProfilePicture().catch(() => undefined);
    return () => {
      active = false;
    };
  }, [activeAvatarLookupJid, activeAvatarUrl, chat, chat.chatJid, chatJid, dispatch]);

  if (!chatJid) return <EmptyRoom />;

  const isLoading = !!state.chatLoading[chatJid];
  const display = displayNameForChat(chat);
  const currentAvatarUrl = activeAvatarUrl;
  const leadFunnel = lead ? state.funnels.find((item) => item.id === lead.funnelId) || null : null;
  const leadStage = lead && leadFunnel ? leadFunnel.stages.find((item) => item.id === lead.stage) || null : null;
  const hasMoreMessages = historyHasMore[chat.chatJid] ?? messages.length >= MESSAGE_PAGE_LIMIT;
  const isOlderLoading = !!olderLoading[chat.chatJid];

  async function refreshMessages() {
    try {
      const next = await fetchMessages(chat.chatJid, { limit: MESSAGE_PAGE_LIMIT, aliasJids: historyAliasJids });
      dispatch({ type: "SET_CHAT_MESSAGES", chatJid: chat.chatJid, messages: next });
      setHistoryHasMore((prev) => ({ ...prev, [chat.chatJid]: next.length >= MESSAGE_PAGE_LIMIT }));
      showToast("Atualizado", "info");
    } catch (err) {
      showToast(err instanceof Error ? err.message : "Falha ao atualizar", "error");
    }
  }

  async function loadOlderMessages() {
    const beforeTs = oldestLoadedMessageTs(messages);
    if (!beforeTs || isOlderLoading || !hasMoreMessages) return;
    setOlderLoading((prev) => ({ ...prev, [chat.chatJid]: true }));
    try {
      const older = await fetchMessages(chat.chatJid, {
        beforeTs: beforeTs,
        limit: MESSAGE_PAGE_LIMIT,
        aliasJids: historyAliasJids,
      });
      const merged = mergeMessagePages(messages, older);
      dispatch({ type: "SET_CHAT_MESSAGES", chatJid: chat.chatJid, messages: merged });
      setHistoryHasMore((prev) => ({
        ...prev,
        [chat.chatJid]: older.length >= MESSAGE_PAGE_LIMIT && merged.length > messages.length,
      }));
    } catch (err) {
      showToast(err instanceof Error ? err.message : "Falha ao carregar mensagens anteriores", "error");
    } finally {
      setOlderLoading((prev) => ({ ...prev, [chat.chatJid]: false }));
    }
  }

  async function loadProfilePicture() {
    if (!chat.chatJid) return;
    setAvatarLoading((prev) => ({ ...prev, [chat.chatJid]: true }));
    try {
      const lookupJid = avatarLookupJidForChat(chat);
      const nextUrl = await getAvatarUrl(lookupJid, { force: true });
      setAvatarUrls((prev) => ({ ...prev, [chat.chatJid]: nextUrl }));
      dispatch({ type: "PATCH_CHAT", chat: avatarPatchForChat(chat, nextUrl) });
      showToast(nextUrl ? "Foto carregada" : "Contato sem foto visivel", "info");
    } catch (err) {
      showToast(err instanceof Error ? err.message : "Falha ao buscar foto", "error");
    } finally {
      setAvatarLoading((prev) => ({ ...prev, [chat.chatJid]: false }));
    }
  }

  async function ensureShortcutsLoaded(): Promise<TextShortcut[]> {
    if ((state.shortcuts || []).length) return state.shortcuts;
    if (!shortcutLoadRef.current) {
      shortcutLoadRef.current = fetchShortcuts()
        .then((items) => {
          patch({ shortcuts: items });
          return items;
        })
        .finally(() => {
          shortcutLoadRef.current = null;
        });
    }
    return await shortcutLoadRef.current;
  }

  function handleChatResolved(resolvedChat: WhatsappChat, previousChatJid: string) {
    const nextChat = { ...chat, ...resolvedChat };
    const chatMessages = { ...state.chatMessages };
    if (previousChatJid !== nextChat.chatJid) {
      const previousMessages = chatMessages[previousChatJid] || [];
      if (previousMessages.length && !chatMessages[nextChat.chatJid]) {
        chatMessages[nextChat.chatJid] = previousMessages;
      }
      delete chatMessages[previousChatJid];
    }
    patch({
      activeChatJid: nextChat.chatJid,
      chats: sortChatsForList([
        nextChat,
        ...state.chats.filter((item) => (
          item.chatJid !== previousChatJid && item.chatJid !== nextChat.chatJid
        )),
      ]),
      chatMessages,
      composer: { chatJid: nextChat.chatJid, text: "" },
    });
  }

  function scheduleCadenceForChatLead() {
    if (!lead) return;
    patch({
      panel: "calendar",
      cadenceDraftLeadId: String(lead.id),
      cadenceSchedulerOpen: true,
    });
  }

  return (
    <section className="wa-chat-room">
      <RoomHeader
        chat={chat}
        display={display}
        lead={lead}
        tags={state.chatTags || []}
        onBack={() => patch({ activeChatJid: "" })}
        onShowLead={() => patch(buildShowLeadPatch(lead))}
        onScheduleCadence={scheduleCadenceForChatLead}
        onOpenTagPicker={(position) => setTagPicker(position)}
        onRefresh={() => void refreshMessages()}
        avatarUrl={currentAvatarUrl}
        avatarLoading={!!avatarLoading[chat.chatJid]}
        onLoadAvatar={() => void loadProfilePicture()}
        onError={(message) => showToast(message, "error")}
        onInfo={(message) => showToast(message, "info")}
      />
      <CentralLeadCard
        chatJid={chat.chatJid}
        isGroup={!!chat.isGroup}
        onLeadFound={applyCentralLeadToChat}
        fallback={(
          <KanbanLeadCard
            lead={lead}
            funnelTitle={leadFunnel?.title || ""}
            stageTitle={leadStage?.title || ""}
          />
        )}
      />
      <MessagesPane
        messages={messages}
        chat={chat}
        isLoading={isLoading}
        hasMoreMessages={hasMoreMessages}
        olderLoading={isOlderLoading}
        onLoadOlder={loadOlderMessages}
      />
      <Composer
        chat={chat}
        lead={lead}
        shortcuts={state.shortcuts || []}
        text={state.composer?.chatJid === chat.chatJid ? state.composer.text || "" : ""}
        recording={!!state.composer?.recording}
        onLoadShortcuts={() => ensureShortcutsLoaded()}
        onTextChange={(text) => patch({ composer: { chatJid: chat.chatJid, text } })}
        onClearText={() => patch({ composer: { chatJid: chat.chatJid, text: "" } })}
        onRestoreText={(text) => patch({ composer: { chatJid: chat.chatJid, text } })}
        onRecordingChange={(recording) => patch({ composer: { chatJid: chat.chatJid, text: state.composer?.text || "", recording } })}
        onChatResolved={handleChatResolved}
        onError={(message) => showToast(message, "error")}
        onSuccess={(message) => showToast(message, "success")}
      />
      {tagPicker ? (
        <TagPicker
          chat={chat}
          tags={state.chatTags || []}
          position={tagPicker}
          onClose={() => setTagPicker(null)}
          onError={(message) => showToast(message, "error")}
        />
      ) : null}
    </section>
  );
}

function EmptyRoom() {
  return (
    <section className="wa-chat-room wa-chat-empty">
      <div className="wa-empty-card">
        <div className="wa-empty-illustration">...</div>
        <h2>Selecione uma conversa</h2>
        <p>Suas mensagens aparecem aqui em tempo real.</p>
      </div>
    </section>
  );
}
